(() => {
  if (document.documentElement.dataset.shopdeWorldNavigation === 'true') return;
  document.documentElement.dataset.shopdeWorldNavigation = 'true';
  const breakpoint = window.matchMedia('(min-width: 990px)');
  const worldKeys = new Set(['hygiene', 'gesundheit', 'tierbedarf', 'fahrrad-sport', 'elektronik-haushalt', 'garten-heimwerken', 'wohnen-deko', 'mode-accessoires', 'branchen']);
  let closeCurrentMenu = () => {};
  let restoreBackground = () => {};

  const hasKeyboardFocusWithin = element => element.contains(document.activeElement)
    && document.activeElement.matches(':focus-visible');

  function initMenuProducts(dropdown) {
    const widget = dropdown.querySelector('[data-shopde-menu-products]');
    if (!widget) return;
    const cardsContainer = widget.querySelector('.shopde-world-featured__cards');
    const controls = widget.querySelector('.shopde-world-featured__controls');
    const loading = widget.querySelector('.shopde-world-featured__loading');
    const pauseButton = widget.querySelector('[data-menu-products-pause]');
    const lifecycle = new AbortController();
    const listen = (element, type, callback) => element.addEventListener(type, callback, { signal: lifecycle.signal });
    let cards = [...cardsContainer.querySelectorAll('[data-menu-product]')];
    let offset = 0;
    let timer;
    let pending;
    let loaded = widget.dataset.explicit === 'true';
    let hovered = false;
    let paused = false;
    const autoplay = widget.dataset.autoplay === 'true';
    const interval = Math.max(6, Number(widget.dataset.interval) || 8) * 1000;
    const canPlay = () => autoplay && cards.length > 2 && dropdown.open && breakpoint.matches
      && !document.hidden && !hovered && !paused && !hasKeyboardFocusWithin(widget);
    const schedule = () => {
      clearTimeout(timer);
      if (canPlay()) timer = setTimeout(() => { show(offset + 2); }, interval);
    };
    const show = next => {
      offset = cards.length ? (next + cards.length) % cards.length : 0;
      cards.forEach((card, index) => { card.hidden = index !== offset && index !== (offset + 1) % cards.length; });
      widget.hidden = cards.length === 0;
      controls.hidden = cards.length <= 2;
      // Cards swap without motion; the pause control is available for every autoplay setting.
      pauseButton.hidden = !autoplay;
      schedule();
    };
    const load = async () => {
      if (loaded || pending || !breakpoint.matches) return;
      widget.hidden = false;
      loading.hidden = false;
      widget.setAttribute('aria-busy', 'true');
      const sources = [...new Set([...widget.querySelectorAll('[data-menu-product-source]')].map(source => source.dataset.menuProductSource))]
        .map(source => { try { return new URL(source, window.location.origin); } catch { return null; } })
        .filter(url => url && url.origin === window.location.origin && /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/collections\/[^/]+(?:\/[^/]+)?\/?$/i.test(url.pathname)
          && !/\/collections\/(?:all|all-products)\/?$/.test(url.pathname))
        .slice(0, 4);
      pending = Promise.allSettled(sources.map(async url => {
        url.searchParams.delete('view');
        url.searchParams.set('section_id', 'shopde-menu-products');
        const preview = window.Shopify?.theme;
        if (preview && ['development', 'unpublished'].includes(preview.role)) url.searchParams.set('preview_theme_id', preview.id);
        const response = await fetch(url, { signal: lifecycle.signal });
        if (!response.ok) throw new Error('Menu products unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return [...html.querySelectorAll('[data-menu-products-response] [data-menu-product]')];
      }));
      const results = await pending;
      if (lifecycle.signal.aborted) return;
      const seen = new Set();
      // Interleave categories so one category cannot fill the entire selection.
      const groups = results.filter(result => result.status === 'fulfilled').map(result => result.value);
      const pool = [];
      for (let index = 0; index < 4; index++) groups.forEach(group => {
        const card = group[index];
        if (card && !seen.has(card.dataset.menuProduct)) { seen.add(card.dataset.menuProduct); pool.push(card); }
      });
      cards = pool.slice(0, 8);
      cardsContainer.replaceChildren(...cards);
      loaded = cards.length > 0;
      pending = null;
      loading.hidden = true;
      widget.removeAttribute('aria-busy');
      show(0);
    };
    listen(dropdown, 'toggle', () => {
      if (dropdown.open && breakpoint.matches) { load(); schedule(); }
      else { hovered = false; clearTimeout(timer); }
    });
    listen(cardsContainer, 'pointerenter', () => { hovered = true; schedule(); });
    listen(cardsContainer, 'pointerleave', () => { hovered = false; schedule(); });
    listen(widget, 'focusin', schedule);
    listen(widget, 'focusout', () => queueMicrotask(schedule));
    listen(document, 'visibilitychange', schedule);
    listen(breakpoint, 'change', schedule);
    listen(widget.querySelector('[data-menu-products-previous]'), 'click', () => show(offset - 2));
    listen(widget.querySelector('[data-menu-products-next]'), 'click', () => show(offset + 2));
    listen(pauseButton, 'click', () => {
      paused = !paused;
      pauseButton.setAttribute('aria-pressed', String(paused));
      pauseButton.setAttribute('aria-label', paused ? widget.dataset.playLabel : widget.dataset.pauseLabel);
      pauseButton.querySelector('span').textContent = paused ? '\u25b6' : '\u2161';
      schedule();
    });
    dropdown._productsCleanup = () => { clearTimeout(timer); lifecycle.abort(); };
    show(0);
    if (dropdown.open && breakpoint.matches) load();
  }

  function updateActiveWorld() {
    const url = new URL(window.location.href);
    const view = url.searchParams.get('view') || '';
    const legacyWorld = view === 'produktwelt' ? url.searchParams.get('welt') : '';
    if (legacyWorld && worldKeys.has(legacyWorld)) {
      url.searchParams.set('view', 'produktwelt-' + legacyWorld);
      url.searchParams.delete('welt');
      window.location.replace(url.href);
      return;
    }
    const world = document.querySelector('[data-shopde-active-world]')?.dataset.shopdeActiveWorld
      || document.querySelector('[data-shopde-product-market]')?.dataset.shopdeProductMarket
      || document.querySelector('[data-shopde-context-world]')?.dataset.shopdeContextWorld
      || (view.startsWith('produktwelt-') && worldKeys.has(view.slice(12)) ? view.slice(12) : '');
    document.querySelectorAll('[data-shopde-market]').forEach(link => {
      const target = new URL(link.href, window.location.origin);
      const isActive = world ? link.dataset.shopdeMarket === world
        : link.dataset.shopdeMarket === 'alle' && target.pathname.replace(/\/$/, '') === url.pathname.replace(/\/$/, '') && !view && !url.searchParams.get('q');
      link.classList.toggle('is-active', isActive);
      if (isActive) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
    document.querySelectorAll('[data-shopde-mobile-world]').forEach(card => {
      card.classList.toggle('is-active', card.dataset.shopdeMobileWorld === world);
    });
  }

  function initShopdeNavigation() {
    updateActiveWorld();
    // Keep authentication on the real shop host and return to this preview theme.
    // Local /account redirects are not customer-login endpoints of the CLI proxy.
    const previewTheme = window.Shopify?.theme;
    if (previewTheme && ['development', 'unpublished'].includes(previewTheme.role) && /^\d+$/.test(String(previewTheme.id))) {
      document.querySelectorAll('[data-shopde-account-login]').forEach(link => {
        const login = new URL(link.href, window.location.origin);
        const returnTo = login.searchParams.get('return_to');
        if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
          const target = new URL(returnTo, login.origin);
          target.searchParams.set('preview_theme_id', String(previewTheme.id));
          login.searchParams.set('return_to', target.pathname + target.search);
          link.href = login.href;
        }
      });
    }
    document.querySelectorAll('.shopde-mega-dropdown').forEach(dropdown => {
      if (dropdown.dataset.initialized) return;
      dropdown.dataset.initialized = 'true';
      initMenuProducts(dropdown);
      const summary = dropdown.querySelector('summary');
      const item = dropdown.closest('.shopde-mega-item');
      const setOpen = open => {
        if (!open && dropdown.contains(document.activeElement) && !hasKeyboardFocusWithin(dropdown)) {
          document.activeElement.blur();
        }
        dropdown.open = open;
        summary.setAttribute('aria-expanded', String(open));
      };
      const closeOthers = () => {
        document.querySelectorAll('.shopde-mega-dropdown[open]').forEach(other => {
          if (other !== dropdown) {
            other.open = false;
            other.querySelector('summary').setAttribute('aria-expanded', 'false');
          }
        });
      };
      summary.setAttribute('aria-expanded', String(dropdown.open));
      dropdown.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(dropdown.open));
        if (dropdown.open) closeOthers();
      });
      item.addEventListener('pointerenter', event => {
        if (breakpoint.matches && event.pointerType === 'mouse') { closeOthers(); setOpen(true); }
      });
      item.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse' && !hasKeyboardFocusWithin(item)) setOpen(false);
      });
      item.addEventListener('focusout', event => {
        if (!item.contains(event.relatedTarget)) setOpen(false);
      });
      item.addEventListener('keydown', event => {
        if (event.key === 'Escape' && dropdown.open) {
          event.preventDefault(); setOpen(false); summary.focus();
        }
      });
    });

    document.querySelectorAll('.shopde-account-menu').forEach(account => {
      if (account.dataset.hoverInitialized) return;
      account.dataset.hoverInitialized = 'true';
      const summary = account.querySelector(':scope > summary');
      account.addEventListener('pointerenter', event => {
        if (breakpoint.matches && event.pointerType === 'mouse') account.open = true;
      });
      account.addEventListener('pointerleave', () => {
        if (breakpoint.matches && !account.contains(document.activeElement)) account.open = false;
      });
      account.addEventListener('focusout', event => {
        if (!account.contains(event.relatedTarget)) account.open = false;
      });
      account.addEventListener('keydown', event => {
        if (event.key === 'Escape' && account.open) { account.open = false; summary?.focus(); }
      });
    });

    const menu = document.getElementById('Shopde-Mobile-Menu');
    if (!menu || menu.dataset.initialized) return;
    closeCurrentMenu(false);
    menu.dataset.initialized = 'true';
    const ownerSection = menu.closest('.shopify-section');
    menu.dataset.ownerSection = ownerSection?.id || '';
    const bottomNavigation = document.querySelector('[data-shopde-bottom-navigation]');
    if (bottomNavigation) bottomNavigation.dataset.ownerSection = menu.dataset.ownerSection;
    document.body.appendChild(menu);
    if (bottomNavigation) document.body.appendChild(bottomNavigation);
    const panel = menu.querySelector('.shopde-mobile-menu__panel');
    const menuBody = menu.querySelector('.shopde-mobile-menu__body');
    const menuDetails = [...menu.querySelectorAll('details')];
    const detailKey = detail => {
      const path = [];
      let current = detail;
      while (current && menu.contains(current)) {
        path.unshift(current.dataset.shopdeMobileWorld || current.querySelector(':scope > summary').textContent.replace(/\s+/g, ' ').trim());
        current = current.parentElement.closest('details');
      }
      return JSON.stringify(path);
    };
    const detailKeys = new Map(menuDetails.map(detail => [detail, detailKey(detail)]));
    const returnKey = 'shopdeMobileMenu';
    let leavingFromMenu = false;
    let returnFrame;
    const writeReturnState = snapshot => {
      try {
        const state = window.history.state;
        if (state !== null && Object.prototype.toString.call(state) !== '[object Object]') return;
        if (snapshot) {
          window.history.replaceState({ ...state, [returnKey]: snapshot }, '');
        } else if (state && Object.prototype.hasOwnProperty.call(state, returnKey)) {
          const next = { ...state };
          delete next[returnKey];
          window.history.replaceState(next, '');
        }
      } catch {}
    };
    const rememberMenu = () => {
      if (breakpoint.matches || menu.hidden) return;
      writeReturnState({
        version: 1,
        owner: menu.dataset.ownerSection,
        expanded: menuDetails.filter(detail => detail.open).map(detail => detailKeys.get(detail)),
        scrollTop: menuBody.scrollTop
      });
    };
    // Keep the custom navigation heading separate from app menu mount targets.
    // Only one product world expands at a time; nested categories stay independent.
    const mobileWorlds = [...menu.querySelectorAll('.shopde-mobile-menu__body > details')];
    mobileWorlds.forEach(world => world.addEventListener('toggle', () => {
      if (world.open) mobileWorlds.forEach(other => { if (other !== world) other.open = false; });
    }));
    let lastFocused = null;
    const openButtons = () => document.querySelectorAll('[data-shopde-menu-open]');
    const setExpanded = open => openButtons().forEach(button => button.setAttribute('aria-expanded', String(open)));
    const closeMenu = (restoreFocus = true, preserveReturn = false) => {
      if (!preserveReturn) {
        leavingFromMenu = false;
        writeReturnState(null);
      }
      const wasOpen = !menu.hidden;
      if (bottomNavigation && panel.contains(bottomNavigation)) document.body.appendChild(bottomNavigation);
      menu.hidden = true;
      document.body.classList.remove('shopde-menu-open');
      setExpanded(false);
      restoreBackground();
      restoreBackground = () => {};
      if (wasOpen && restoreFocus && lastFocused?.isConnected) lastFocused.focus({ preventScroll: true });
    };
    const openMenu = (restoring = false) => {
      if (breakpoint.matches || !menu.hidden) return;
      lastFocused = restoring
        ? [...openButtons()].find(button => button.getClientRects().length > 0) || document.activeElement
        : document.activeElement;
      if (bottomNavigation) panel.appendChild(bottomNavigation);
      menu.hidden = false;
      document.body.classList.add('shopde-menu-open');
      setExpanded(true);
      const previousStates = [];
      let child = menu;
      while (child.parentElement) {
        for (const sibling of child.parentElement.children) {
          if (sibling !== child && sibling instanceof HTMLElement && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
            previousStates.push([sibling, sibling.inert]);
            sibling.inert = true;
          }
        }
        child = child.parentElement;
        if (child === document.body) break;
      }
      restoreBackground = () => previousStates.forEach(([element, inert]) => { element.inert = inert; });
      menu.querySelector('button[data-shopde-menu-close]')?.focus({ preventScroll: true });
    };
    const restoreMenu = () => {
      if (breakpoint.matches) return;
      try {
        const saved = window.history.state?.[returnKey];
        if (saved?.version !== 1 || saved.owner !== menu.dataset.ownerSection || !Array.isArray(saved.expanded)) return;
        const expanded = new Set(saved.expanded);
        menuDetails.forEach(detail => { detail.open = expanded.has(detailKeys.get(detail)); });
        openMenu(true);
        if (Number.isFinite(saved.scrollTop)) menuBody.scrollTop = Math.max(0, saved.scrollTop);
      } catch {}
    };
    const historyReturn = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
    const showMenu = event => {
      leavingFromMenu = false;
      if (!event.persisted && !historyReturn) return;
      cancelAnimationFrame(returnFrame);
      returnFrame = requestAnimationFrame(restoreMenu);
    };
    const hideMenu = () => {
      if (!menu.hidden) rememberMenu();
      else if (!leavingFromMenu) writeReturnState(null);
    };
    closeCurrentMenu = closeMenu;
    menu.addEventListener('click', event => {
      if (event.target.closest('[data-shopde-menu-close]')) closeMenu();
      else {
        const link = event.target.closest('a[href]');
        if (!link) return;
        const destination = new URL(link.href, window.location.origin);
        const navigating = !breakpoint.matches && !event.defaultPrevented && event.button === 0 &&
          !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey &&
          !link.hasAttribute('download') && (!link.target || link.target === '_self') &&
          destination.origin === window.location.origin &&
          (destination.pathname !== window.location.pathname || destination.search !== window.location.search);
        if (navigating) rememberMenu();
        leavingFromMenu = navigating;
        closeMenu(false, navigating);
      }
    });
    menu.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); closeMenu(); }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(panel.querySelectorAll('a[href], button, input:not([type="hidden"]), summary, [tabindex="0"]'))
        .filter(element => element.getClientRects().length > 0 && !element.disabled);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); panel.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    openButtons().forEach(button => button.addEventListener('click', () => {
      if (menu.hidden) openMenu();
      else closeMenu();
    }));
    window.addEventListener('pageshow', showMenu);
    window.addEventListener('pagehide', hideMenu);
    menu._shopdeReturnCleanup = () => {
      window.removeEventListener('pageshow', showMenu);
      window.removeEventListener('pagehide', hideMenu);
      cancelAnimationFrame(returnFrame);
    };
    if (historyReturn) restoreMenu();

    const headerCart = document.getElementById('cart-icon-bubble');
    const bottomCart = document.querySelector('.shopde-mobile-bottom [data-shopde-cart-trigger]');
    if (headerCart && bottomCart) {
      const syncCount = () => {
        const count = headerCart.querySelector('.cart-count-bubble [aria-hidden="true"]')?.textContent.trim() || '';
        let badge = bottomCart.querySelector('.shopde-mobile-bottom__badge');
        if (count) {
          if (!badge) {
            badge = document.createElement('span'); badge.className = 'shopde-mobile-bottom__badge';
            bottomCart.querySelector('.shopde-mobile-bottom__icon-wrap').appendChild(badge);
          }
          badge.textContent = count;
        } else badge?.remove();
        bottomCart.setAttribute('aria-label', count ? `${bottomCart.dataset.cartLabel}: ${count}` : bottomCart.dataset.cartLabel);
      };
      syncCount();
      const observer = new MutationObserver(syncCount);
      observer.observe(headerCart, { childList: true, subtree: true, characterData: true });
      menu._shopdeCartObserver = observer;
      bottomCart.addEventListener('click', event => {
        if (document.querySelector('cart-drawer')) { event.preventDefault(); closeMenu(false); headerCart.click(); }
      });
    }
  }

  document.addEventListener('click', event => {
    document.querySelectorAll('.shopde-mega-dropdown[open]').forEach(dropdown => {
      if (!dropdown.closest('.shopde-mega-item').contains(event.target)) dropdown.open = false;
    });
  });
  window.addEventListener('scroll', () => {
    if (!breakpoint.matches) return;
    document.querySelectorAll('.shopde-mega-dropdown[open]').forEach(dropdown => {
      if (dropdown.contains(document.activeElement)) {
        if (hasKeyboardFocusWithin(dropdown)) dropdown.querySelector('summary').focus({ preventScroll: true });
        else document.activeElement.blur();
      }
      dropdown.open = false;
    });
  }, { passive: true });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.shopde-mega-dropdown[open]').forEach(dropdown => {
      dropdown.open = false;
      dropdown.querySelector('summary').focus();
    });
  });
  breakpoint.addEventListener('change', () => {
    closeCurrentMenu();
    document.querySelectorAll('.shopde-mega-dropdown[open], .shopde-account-menu[open]').forEach(dropdown => { dropdown.open = false; });
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.shopde-mega-dropdown').forEach(dropdown => dropdown._productsCleanup?.());
    const menu = document.getElementById('Shopde-Mobile-Menu');
    if (menu && menu.dataset.ownerSection === event.target.id) {
      closeCurrentMenu(false);
      menu._shopdeReturnCleanup?.();
      menu._shopdeCartObserver?.disconnect();
      document.querySelector('[data-shopde-bottom-navigation]')?.remove();
      menu.remove();
    }
  });
  document.addEventListener('shopify:section:load', initShopdeNavigation);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initShopdeNavigation, { once: true });
  else initShopdeNavigation();
})();
